import {
  FC,
  FormEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  faCheckCircle,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { faPlusCircle } from '@fortawesome/pro-solid-svg-icons';
import {
  Button,
  createBemBlockBuilder,
  InPageNotification,
  Loading,
} from '@ebsco-ui/ebsco-ui';

import {
  Dropdown,
  DropdownItem,
  GuestUserModalState,
  Modal,
  ModalHeader,
  Notification,
  Toast,
} from '@app/components';
import { useAppContext } from '@app/contexts';
import {
  Project,
  queryKeys,
  useAddInstancesToProjectMutation,
  useProjectsFetch,
  useQueryParams,
} from '@app/hooks';
import { sharedMessages } from '@app/translations';

import { AddToProjectModalContext } from './AddToProjectModalContext';
import { messages } from './messages';

import './AddToProjectModal.scss';

const cnBem = createBemBlockBuilder(['addToProjectModal']);

const newProjectDropdownItem = {
  value: 'new',
  label: (
    <FormattedMessage id="projects.newProject" defaultMessage="New project" />
  ) as unknown as string,
  icon: faPlusCircle,
};

export const AddToProjectModal: FC = () => {
  const { isLoggedIn } = useAppContext();
  const [selectedProject, setSelectedProject] = useState<DropdownItem | null>(
    null
  );
  const { $t } = useIntl();
  const projectsQuery = useProjectsFetch();
  const addInstancesToProjectMutation = useAddInstancesToProjectMutation();
  const formId = 'add-project-form';
  const { state: modalState, dispatch: updateModalState } = useContext(
    AddToProjectModalContext
  );
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAddToProject: shouldOpenModal } = useQueryParams();

  const addToProjectMessage = $t(sharedMessages.addToProject);
  const signInToAddToProjectMessage = $t({
    id: 'projects.signInToAddToProject',
    defaultMessage: 'Please sign in to add this item to a project.',
  });
  const addNowMessage = $t({
    id: 'projects.addNow',
    defaultMessage: 'Add now',
  });

  const closeModal = useCallback(() => {
    setSelectedProject(null);
    updateModalState({ type: 'CLOSE_MODAL' });
    addInstancesToProjectMutation.reset();

    if (shouldOpenModal) {
      navigate(location.pathname);
    }
  }, [
    updateModalState,
    addInstancesToProjectMutation,
    shouldOpenModal,
    navigate,
    location.pathname,
  ]);

  const toastIcons = useMemo(
    () => ({
      success: faCheckCircle,
      error: faExclamationTriangle,
    }),
    []
  );

  useEffect(() => {
    const status = addInstancesToProjectMutation.status;

    if (status === 'success') {
      Toast({
        type: status,
        title: $t(messages[status]),
        icon: toastIcons[status],
      });

      closeModal();
      queryClient.resetQueries({
        queryKey: [
          queryKeys.projectDetails,
          addInstancesToProjectMutation.variables?.projectId,
        ],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addInstancesToProjectMutation.status,
    toastIcons,
    updateModalState,
    closeModal,
  ]);

  const renderNotification = () => {
    const props = {
      className: cnBem('__notification'),
      header: $t(sharedMessages.somethingWentWrong),
      variant: InPageNotification.VARIANT.error,
    };

    if (projectsQuery.isError) {
      return (
        <Notification
          {...props}
          description={$t(
            {
              id: 'projects.weCouldNotLoadProjects',
              defaultMessage:
                'We could not load your projects. <button>Please, try again</button>.',
            },
            {
              button: chunks => (
                <Button
                  styleType="flat-icon"
                  onClick={() => projectsQuery.refetch()}
                >
                  {chunks}
                </Button>
              ),
            }
          )}
        />
      );
    }

    if (addInstancesToProjectMutation.isError) {
      const errorMessage =
        addInstancesToProjectMutation.error.response?.data?.errorMessage ?? '';
      const isDuplicate = errorMessage.includes('Duplicate instance');

      if (isDuplicate) {
        return (
          <Notification
            className={cnBem('__notification')}
            header={$t({
              id: 'projects.itemAlreadyExists',
              defaultMessage:
                'This item already exists in the selected project.',
            })}
            variant={InPageNotification.VARIANT.information}
            description={
              <span data-auto="info-message">
                {$t({
                  id: 'projects.addItemToDifferentProject',
                  defaultMessage:
                    'Add this item to a different project or create a new project',
                })}
              </span>
            }
          />
        );
      }

      return (
        <Notification
          {...props}
          description={
            <span data-auto="error-message">
              {$t({
                id: 'projects.itemWasNotAddedToProject',
                defaultMessage:
                  'This item was not added to your project. Please try again.',
              })}
            </span>
          }
        />
      );
    }

    return null;
  };

  const renderModalBody = () => {
    if (!isLoggedIn) {
      return (
        <GuestUserModalState
          message={signInToAddToProjectMessage}
          redirectBackRoute={modalState.redirectBackRoute}
        />
      );
    }

    return (
      <>
        {renderNotification()}
        {(projectsQuery.isFetching ||
          addInstancesToProjectMutation.isLoading) && (
          <Loading loadingMessage={$t(sharedMessages.loadingMessage)} />
        )}
        <Dropdown
          id="choose-project-dropdown"
          styleType="standard"
          placeholder={$t({
            id: 'projects.chooseProject',
            defaultMessage: 'Choose a project',
          })}
          items={[
            newProjectDropdownItem,
            ...getProjectsDropdownValues(projectsQuery.data),
          ]}
          selectedItem={selectedProject as DropdownItem}
          onSelectedItemChange={({ selectedItem }) =>
            setSelectedProject(selectedItem ?? null)
          }
        />
      </>
    );
  };

  const getProjectsDropdownValues = (projects: Project[] = []) =>
    projects.map(({ id, name }) => ({ id, label: name }));

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    addInstancesToProjectMutation.mutate({
      instanceIds: [{ instanceId: modalState.id as string }],
      projectId: selectedProject?.id as string,
    });
  };

  return (
    <Modal
      isOpen={modalState.isOpen}
      modalTitle={addToProjectMessage}
      className={cnBem()}
      onClose={closeModal}
    >
      <ModalHeader
        title={addToProjectMessage}
        className={cnBem('__header')}
        onClose={closeModal}
      />
      <Modal.Body className={cnBem('__body')}>
        <form id={formId} onSubmit={handleSubmit}>
          {renderModalBody()}
        </form>
      </Modal.Body>
      {isLoggedIn && (
        <Modal.Footer className={cnBem('__footer')}>
          <Button
            form={formId}
            title={$t(sharedMessages.cancel)}
            styleType="text"
            onClick={closeModal}
          >
            {$t(sharedMessages.cancel)}
          </Button>
          <Button
            form={formId}
            title={addNowMessage}
            styleType="default"
            type="submit"
            disabled={!selectedProject}
          >
            {addNowMessage}
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};
